import { useState, useEffect, useRef } from 'react';
import { Modal } from './App';

const API = window.__API_URL__ || 'http://127.0.0.1:8000';

function genererNomFichier(type, doc) {
  if (type === 'devis') return `${doc?.reference || 'devis'}.pdf`;
  if (type === 'facture') return `${doc?.numero || 'facture'}.pdf`;
  if (type === 'avoir') return `${doc?.numero || 'avoir'}.pdf`;
  return 'document.pdf';
}

function endpointPdf(type, docId) {
  if (type === 'devis') return `/devis/${docId}/pdf`;
  if (type === 'facture') return `/factures/${docId}/pdf`;
  if (type === 'avoir') return `/avoirs/${docId}/pdf`;
  return null;
}

function ModalEmail({ emailDocument, apiFetch, onClose, showMsg }) {
  const { type, document: doc, client } = emailDocument;

  const [modeles, setModeles] = useState([]);
  const [modeleSelectionne, setModeleSelectionne] = useState(null);
  const [destinataire, setDestinataire] = useState(client?.email || '');
  const [sujet, setSujet] = useState('');
  const [corps, setCorps] = useState('');
  const [piecesJointes, setPiecesJointes] = useState([]); // [{id, name, blob, source}]
  const [generationPDF, setGenerationPDF] = useState(true);
  const [erreurPDF, setErreurPDF] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [chargementModeles, setChargementModeles] = useState(true);

  const fileInputRef = useRef(null);
  const idCounterRef = useRef(0);

  // Charger les modèles
  useEffect(() => {
    apiFetch(`${API}/emails/modeles`)
      .then(r => r.json())
      .then(data => {
        setModeles(data || []);
        setChargementModeles(false);
      })
      .catch(() => {
        console.error('Erreur chargement modèles');
        setChargementModeles(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Récupération auto des pièces jointes au montage :
  //  1) PDF du devis/facture/avoir généré côté backend (WeasyPrint)
  //  2) PDF des CGV (asset statique)
  // L'ancienne approche html2pdf/html2canvas côté frontend produisait des PDF vides
  // après le passage à React 19 — abandonnée le 2026-05-11.
  useEffect(() => {
    let annule = false;

    const recupererPiecesAuto = async () => {
      const pieces = [];

      // 1) PDF du document
      try {
        const path = endpointPdf(type, doc?.id);
        if (path) {
          const r = await apiFetch(`${API}${path}`);
          if (r.ok) {
            const blob = await r.blob();
            if (!annule) {
              pieces.push({
                id: ++idCounterRef.current,
                name: genererNomFichier(type, doc),
                blob,
                source: type,
              });
            }
          } else {
            console.error('Erreur génération PDF document, status', r.status);
            if (!annule) setErreurPDF('La génération automatique du PDF a échoué. Vous pouvez l\'ajouter manuellement ci-dessous.');
          }
        }
      } catch (e) {
        console.error('Erreur génération PDF document', e);
        if (!annule) setErreurPDF('La génération automatique du PDF a échoué. Vous pouvez l\'ajouter manuellement ci-dessous.');
      }

      // 2) PDF des CGV
      try {
        const r = await apiFetch(`${API}/emails/cgv-pdf`);
        if (r.ok) {
          const blob = await r.blob();
          if (!annule) {
            pieces.push({
              id: ++idCounterRef.current,
              name: 'CGV_map-menuiserie.pdf',
              blob,
              source: 'cgv',
            });
          }
        }
      } catch (e) {
        console.error('Erreur récupération CGV', e);
      }

      if (!annule) {
        setPiecesJointes(pieces);
        setGenerationPDF(false);
      }
    };

    recupererPiecesAuto();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remplacement des variables {{}}
  const remplacerVariables = (texte) => {
    const nomClient = client?.est_professionnel
      ? (client?.nom_entreprise || client?.nom)
      : `${client?.prenom || ''} ${client?.nom || ''}`.trim();

    const ref = type === 'devis' ? doc?.reference : doc?.numero;
    const montantHt = type === 'devis'
      ? (doc?.version?.montant_ht || 0)
      : (doc?.montant_ht || 0);
    const montantFormate = parseFloat(montantHt).toFixed(2).replace('.', ',') + ' €';

    let dateEcheance = '';
    if (type === 'devis' && doc?.version?.date_echeance_validite) {
      dateEcheance = new Date(doc.version.date_echeance_validite).toLocaleDateString('fr-FR');
    } else if (type === 'facture' && doc?.date_emission) {
      dateEcheance = new Date(doc.date_emission).toLocaleDateString('fr-FR');
    }

    return texte
      .replace(/{{nom_client}}/g, nomClient)
      .replace(/{{reference_document}}/g, ref || '')
      .replace(/{{numero_facture}}/g, doc?.numero || '')
      .replace(/{{reference_devis}}/g, doc?.reference || '')
      .replace(/{{montant_ht}}/g, montantFormate)
      .replace(/{{date_echeance}}/g, dateEcheance);
  };

  const selectionnerModele = (modele) => {
    setModeleSelectionne(modele);
    setSujet(remplacerVariables(modele.sujet));
    setCorps(remplacerVariables(modele.corps));
  };

  const ajouterFichiers = (files) => {
    if (!files || files.length === 0) return;
    const nouvelles = Array.from(files).map(f => ({
      id: ++idCounterRef.current,
      name: f.name,
      blob: f,
      source: 'manuel',
    }));
    setPiecesJointes(prev => [...prev, ...nouvelles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const retirerPJ = (id) => {
    setPiecesJointes(prev => prev.filter(p => p.id !== id));
  };

  const envoyer = async () => {
    if (!destinataire) {
      alert('⚠️ Veuillez entrer une adresse email');
      return;
    }
    if (!sujet.trim()) {
      alert('⚠️ Veuillez entrer un sujet');
      return;
    }
    if (!corps.trim()) {
      alert('⚠️ Veuillez entrer un message');
      return;
    }

    setEnvoi(true);

    try {
      const fd = new FormData();
      fd.append('destinataire', destinataire);
      fd.append('sujet', sujet);
      fd.append('corps', corps);
      piecesJointes.forEach(pj => {
        fd.append('fichiers', pj.blob, pj.name);
      });

      const r = await apiFetch(`${API}/emails/envoyer`, {
        method: 'POST',
        body: fd,
      });

      if (r.ok) {
        showMsg('✅ Email envoyé !');
        onClose();
      } else {
        const err = await r.json().catch(() => ({}));
        alert(`❌ Erreur : ${err.detail || 'Erreur lors de l\'envoi'}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('❌ Erreur réseau');
    } finally {
      setEnvoi(false);
    }
  };

  const titre = type === 'devis'
    ? `📧 Envoyer le devis — ${doc?.reference || ''}`
    : type === 'facture'
      ? `📧 Envoyer la facture — ${doc?.numero || ''}`
      : `📧 Envoyer l'avoir — ${doc?.numero || ''}`;

  const iconePJ = (source) => {
    if (source === 'cgv') return '📜';
    if (source === 'manuel') return '📎';
    return '📄';
  };

  const styles = {
    section: { marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' },
    label: { fontWeight: 600, color: '#333', fontSize: '14px' },
    input: { padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit', transition: 'border-color 0.2s' },
    select: { padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit', backgroundColor: '#fff', cursor: 'pointer' },
    textarea: { padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit', minHeight: '120px', resize: 'vertical' },
    warning: { padding: '10px 12px', backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '6px', color: '#92400E', fontSize: '13px' },
    erreur: { padding: '10px 12px', backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: '6px', color: '#991B1B', fontSize: '13px' },
    pjList: { display: 'flex', flexDirection: 'column', gap: '6px' },
    pjItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#F3F4F6', borderRadius: '6px', fontSize: '13px' },
    pjBtnRetirer: { background: 'transparent', border: 'none', color: '#DC2626', fontSize: '18px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 },
    btnAjouterPJ: { padding: '8px 12px', background: '#fff', border: '1px dashed #9CA3AF', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', color: '#374151' },
    buttonContainer: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' },
    button: { padding: '10px 20px', borderRadius: '6px', border: 'none', fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' },
    buttonPrimary: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' },
    buttonSecondary: { background: '#f3f4f6', color: '#333' },
  };

  return (
    <Modal title={titre} onClose={onClose} wide>
      {/* Sélecteur de modèle */}
      <div style={styles.section}>
        <label style={styles.label}>📋 Modèle de courrier</label>
        {chargementModeles ? (
          <p style={{ color: '#666', fontSize: '13px' }}>Chargement...</p>
        ) : (
          <select
            value={modeleSelectionne?.id || ''}
            onChange={(e) => {
              const id = parseInt(e.target.value);
              const modele = modeles.find(m => m.id === id);
              if (modele) selectionnerModele(modele);
            }}
            style={styles.select}
          >
            <option value="">-- Choisir un modèle --</option>
            {modeles.map(m => (
              <option key={m.id} value={m.id}>{m.nom}</option>
            ))}
          </select>
        )}
      </div>

      {/* Destinataire */}
      <div style={styles.section}>
        <label style={styles.label}>📧 Destinataire</label>
        {!client?.email && (
          <div style={styles.warning}>
            ⚠️ L'email du client n'est pas renseigné. Veuillez l'entrer manuellement.
          </div>
        )}
        <input
          type="email"
          placeholder="nom@example.com"
          value={destinataire}
          onChange={(e) => setDestinataire(e.target.value)}
          style={styles.input}
        />
      </div>

      {/* Sujet */}
      <div style={styles.section}>
        <label style={styles.label}>Sujet</label>
        <input
          type="text"
          placeholder="Objet du mail..."
          value={sujet}
          onChange={(e) => setSujet(e.target.value)}
          style={styles.input}
        />
      </div>

      {/* Corps */}
      <div style={styles.section}>
        <label style={styles.label}>Message</label>
        <textarea
          placeholder="Votre message..."
          value={corps}
          onChange={(e) => setCorps(e.target.value)}
          style={styles.textarea}
        />
        <p style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
          💡 Les variables {'{{'} nom_client {'}}'}, {'{{'} reference_document {'}}'}, {'{{'} montant_ht {'}}'} seront automatiquement remplacées.
        </p>
      </div>

      {/* Pièces jointes */}
      <div style={styles.section}>
        <label style={styles.label}>📎 Pièces jointes</label>
        {generationPDF && (
          <p style={{ color: '#666', fontSize: '13px' }}>⏳ Génération du PDF en cours...</p>
        )}
        {erreurPDF && <div style={styles.erreur}>{erreurPDF}</div>}
        {!generationPDF && piecesJointes.length === 0 && (
          <p style={{ color: '#666', fontSize: '13px' }}>Aucune pièce jointe.</p>
        )}
        <div style={styles.pjList}>
          {piecesJointes.map(pj => (
            <div key={pj.id} style={styles.pjItem}>
              <span>{iconePJ(pj.source)} {pj.name}</span>
              <button
                type="button"
                onClick={() => retirerPJ(pj.id)}
                style={styles.pjBtnRetirer}
                title="Retirer cette pièce jointe"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          multiple
          onChange={(e) => ajouterFichiers(e.target.files)}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          style={styles.btnAjouterPJ}
        >
          ➕ Ajouter une pièce jointe
        </button>
      </div>

      {/* Boutons */}
      <div style={styles.buttonContainer}>
        <button
          onClick={onClose}
          disabled={envoi}
          style={{ ...styles.button, ...styles.buttonSecondary, opacity: envoi ? 0.5 : 1, cursor: envoi ? 'not-allowed' : 'pointer' }}
        >
          Annuler
        </button>
        <button
          onClick={envoyer}
          disabled={envoi || generationPDF}
          style={{ ...styles.button, ...styles.buttonPrimary, opacity: (envoi || generationPDF) ? 0.7 : 1, cursor: (envoi || generationPDF) ? 'not-allowed' : 'pointer' }}
        >
          {envoi ? '⏳ Envoi en cours...' : '✉️ Envoyer l\'email'}
        </button>
      </div>

    </Modal>
  );
}

export default ModalEmail;
